import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface SnowEffectProps {
  intensity?: number;      // 0-1
  particleCount?: number;
  areaSize?: [number, number]; // width, depth
  color?: string;
  isBlizzard?: boolean;    // More intense, horizontal movement
}

export function SnowEffect({
  intensity = 0.5,
  particleCount = 500,
  areaSize = [20, 20],
  color = '#ffffff',
  isBlizzard = false,
}: SnowEffectProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const particlesRef = useRef<{
    position: THREE.Vector3;
    velocity: THREE.Vector3;
    rotation: number;
    rotationSpeed: number;
    scale: number;
    phase: number;
  }[]>([]);

  const actualCount = Math.floor(particleCount * intensity);

  // Initialize snow flakes
  useMemo(() => {
    particlesRef.current = [];

    for (let i = 0; i < actualCount; i++) {
      particlesRef.current.push({
        position: new THREE.Vector3(
          (Math.random() - 0.5) * areaSize[0],
          Math.random() * 15 + 3,
          (Math.random() - 0.5) * areaSize[1]
        ),
        velocity: new THREE.Vector3(
          isBlizzard ? (Math.random() - 0.3) * 3 : (Math.random() - 0.5) * 0.5,
          -(0.5 + Math.random() * 0.5) * (isBlizzard ? 1.5 : 1),
          isBlizzard ? (Math.random() - 0.5) * 1 : (Math.random() - 0.5) * 0.3
        ),
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 2,
        scale: 0.03 + Math.random() * 0.05,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }, [actualCount, areaSize[0], areaSize[1], isBlizzard]);

  useFrame((state, delta) => {
    if (!meshRef.current) return;

    const time = state.clock.elapsedTime;
    const [width, depth] = areaSize;
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();

    for (let i = 0; i < particlesRef.current.length; i++) {
      const particle = particlesRef.current[i];

      // Gentle swaying motion
      const sway = Math.sin(time * 2 + particle.phase) * 0.3;

      // Update position
      particle.position.x += (particle.velocity.x + sway) * delta;
      particle.position.y += particle.velocity.y * delta;
      particle.position.z += particle.velocity.z * delta;
      particle.rotation += particle.rotationSpeed * delta;

      // Reset when hitting ground
      if (particle.position.y < 0) {
        particle.position.y = 15 + Math.random() * 5;
        particle.position.x = (Math.random() - 0.5) * width;
        particle.position.z = (Math.random() - 0.5) * depth;
      }

      // Wrap around
      if (particle.position.x > width / 2) particle.position.x = -width / 2;
      if (particle.position.x < -width / 2) particle.position.x = width / 2;
      if (particle.position.z > depth / 2) particle.position.z = -depth / 2;
      if (particle.position.z < -depth / 2) particle.position.z = depth / 2;

      // Build transformation matrix
      quaternion.setFromEuler(new THREE.Euler(0, particle.rotation, 0));
      matrix.compose(
        particle.position,
        quaternion,
        new THREE.Vector3(particle.scale, particle.scale, particle.scale)
      );

      meshRef.current!.setMatrixAt(i, matrix);
    }

    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  if (actualCount === 0) return null;

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, actualCount]}>
      {/* Flat hexagon for snow flakes */}
      <circleGeometry args={[1, 6]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={0.8 * intensity}
        side={THREE.DoubleSide}
      />
    </instancedMesh>
  );
}
